import { useCallback, useState } from 'react';
import type { StartCallBody, StartCallResponse } from '@vagusx/clients';
import { getBrowserCallClient } from '@/lib/api-clients';

export type TestCallPhase = 'idle' | 'fetching-token' | 'ready' | 'error';

/**
 * `StartCallBody` is the shared discriminated-union schema from
 * `@vagusx/clients` — the SAME schema the API parses with Zod. Adding
 * a new variant or field is a one-place change and the FE/API stay in
 * sync (no more "field added on the server but never reaches the FE"
 * drift).
 *
 * The hook adds `idempotencyKey` on top because it travels in the HTTP
 * header, not the body. Caller mints it in the parent component and
 * holds it stable across re-renders / remounts — generating it inside
 * this hook would defeat the purpose, since a StrictMode double-mount
 * produces two different keys and the server creates two rooms.
 */
export type TestCallStartOptions = StartCallBody & { idempotencyKey: string };

/**
 * Connection role for the minted token:
 *   - `participant`: full publish+subscribe (browser-test — the operator
 *     talks). The CallPanel publishes the mic.
 *   - `viewer`: subscribe-only (sip-outbound — the operator monitors).
 *     The CallPanel must NOT publish audio.
 */
export type TestCallMode = 'participant' | 'viewer';

export interface TestCallSession {
  phase: TestCallPhase;
  token: string | null;
  serverUrl: string | null;
  interactionId: string | null;
  mode: TestCallMode;
  errorMessage: string | null;
  start: (opts: TestCallStartOptions) => Promise<void>;
  /** Start from any endpoint that answers like `/browser-call/test` (a builder test). */
  startWith: (request: () => Promise<StartCallResponse>) => Promise<void>;
  reset: () => void;
}

/**
 * Fetches a LiveKit access token from the backend. The actual room connection,
 * audio rendering, mic publishing, and autoplay recovery are handled by
 * `@livekit/components-react` primitives (`<LiveKitRoom>`, `<RoomAudioRenderer>`,
 * `<StartAudio>`) so we don't hand-roll any of it here.
 *
 * StrictMode remount safety is the CALLER's job, and it is a send-once guard,
 * not a cancel-after-the-fact one. This hook used to accept an `AbortSignal` so
 * the caller could abort the first mount's fetch from its effect cleanup — but
 * an abort only discards the browser's copy of the response. If the request had
 * already reached the API, the phone still rang. `CallPanel` now guards with a
 * ref so the second request is never sent, and the API refuses a second dial
 * for the same interaction regardless (`InteractionService.claimForDial`).
 */
export function useTestCall(): TestCallSession {
  const [phase, setPhase] = useState<TestCallPhase>('idle');
  const [token, setToken] = useState<string | null>(null);
  const [serverUrl, setServerUrl] = useState<string | null>(null);
  const [interactionId, setInteractionId] = useState<string | null>(null);
  const [mode, setMode] = useState<TestCallMode>('participant');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const startWith = useCallback(
    async (request: () => Promise<StartCallResponse>): Promise<void> => {
      setErrorMessage(null);
      setPhase('fetching-token');
      try {
        const data = await request();
        setToken(data.token);
        setServerUrl(data.url);
        setInteractionId(data.interactionId);
        setMode(data.mode);
        setPhase('ready');
      } catch (err) {
        console.error('[useTestCall] start failed:', err);
        setErrorMessage(err instanceof Error ? err.message : String(err));
        setPhase('error');
      }
    },
    []
  );

  const start = useCallback(
    (opts: TestCallStartOptions): Promise<void> => {
      const { idempotencyKey, ...body } = opts;
      return startWith(() => getBrowserCallClient().startTest(body, { idempotencyKey }));
    },
    [startWith]
  );

  const reset = useCallback(() => {
    setPhase('idle');
    setToken(null);
    setServerUrl(null);
    setInteractionId(null);
    setMode('participant');
    setErrorMessage(null);
  }, []);

  return { phase, token, serverUrl, interactionId, mode, errorMessage, start, startWith, reset };
}
